import { DialogRef } from '@angular/cdk/dialog';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, fromEvent, interval } from 'rxjs';
import { NoconnectComponent } from '../../shared/components/noconnect/noconnect.component';
import { SessionProvider } from './session.provider';

@Injectable({
  providedIn: 'root'
})
export class CheckOnlineService {
  connectivityState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  backendState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isAppVisible$ = new BehaviorSubject<boolean>(!document.hidden);

  offlineEvent: Observable<Event>;
  onlineEvent: Observable<Event>;
  visibilityEvent: Observable<Event>;

  subscriptions: Subscription[] = [];

  isNewVersionAvailable: boolean = false;
  backendIntervalSource = interval(30 * 1000);
  intervalSubscription: Subscription;

  failModal: MatDialogRef<NoconnectComponent> | null = null;

  constructor(
    private zone: NgZone,
    private modalService: MatDialog,
    private session: SessionProvider,
    private router: Router)
  {
    this.subscribeToOnlineStateChanges();
    this.startBackendCheck();
  }

  subscribeToOnlineStateChanges() {
    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');
    this.visibilityEvent = fromEvent(document, 'visibilitychange');

    this.subscriptions.push(this.onlineEvent.subscribe(e => {
      this.backendCheck().then(_ => {
        this.connectivityState$.next(true);
      });
    }));

    this.subscriptions.push(this.offlineEvent.subscribe(e => {
      this.connectivityState$.next(false);
      this.showModalMessageOnConnectionFail();
    }));

    this.subscriptions.push(this.visibilityEvent.subscribe(e => {
      this.isAppVisible$.next(!document.hidden);
    }));
  }

  startBackendCheck(): void {
    this.intervalSubscription?.unsubscribe();

    this.zone.runOutsideAngular(() => {
      this.intervalSubscription = this.backendIntervalSource.subscribe(async () => {
        if (this.isAppVisible$.value === true) {
          try {
            this.sessionExpiryCheck();
            this.backendCheck();
            this.checkForUpdate();
          } catch (error) {
            console.error('Failed to check backend state:', error);
          }
        }
      });
    })
  }

  /**
   * Ha a nyitva hagyott alkalmazásban lejár a munkamenet, kiléptetjük a felhasználót, és a bejelentkezési oldal jelzi ennek okát. (MIRA-2343)
   * Szándékosan nem a session.check()-et hívjuk, mert az új tokent ad, és így a munkamenet soha nem járna le.
   */
  sessionExpiryCheck(): void {
    // bejelentkezés nélkül (pl. nyilvános bérkalkulátor link egy régi, bent maradt tokennel) nincs mit lejáratni
    if (this.session.getUserId() === undefined) {
      return;
    }

    const expiration = this.session.getTokenExpiration();
    if (expiration !== undefined && expiration <= Date.now()) {
      this.zone.run(() => {
        this.session.setRedirectUrl(this.router.url);
        this.session.logout(true);
      });
    }
  }

  async backendCheck():Promise<void> {
    const backendState = await this.baseAddressIsReachable();
    if (backendState !== this.backendState$.value) {
      this.backendState$.next(backendState);
      if (backendState === false && this.isAppVisible$.value === true) {
        this.showModalMessageOnConnectionFail();
      }
    }
  }
  async baseAddressIsReachable(): Promise<boolean> {
    return fetch("", { method: 'HEAD', mode: 'no-cors' })
      .then(function (resp) {
        return resp && (resp.ok || resp.type === 'opaque');
      })
      .catch(function (err) {
        console.warn('[conn test failure]:', err);
        return false;
      });
  }
  async checkForUpdate(autoUpdate: boolean = false): Promise<void> {
    try {
          const resp = await fetch("", { method: 'HEAD', mode: 'no-cors' });
          const serverVersion = resp.headers.get('x-appversion');
          const clientVersion = this.session.getAppVersion();

          if (serverVersion && serverVersion !== clientVersion) {
              console.log('A new version is available.');
              setTimeout(() => {
                if (autoUpdate) {
                  setTimeout(() => {
                    document.location.reload();
                  }, 2000);
                } else {
                  this.session.versionMismatch(serverVersion);
                }
              }, 1000);
          } else {
              console.log('Already on the latest version.');
          }
      } catch (error) {
          console.error('Failed to check for updates:', error);
      }
  }

  showModalMessageOnConnectionFail() {
    if (this.failModal === null || this.failModal.getState() === MatDialogState.CLOSED) {
      this.failModal = this.modalService.open(NoconnectComponent, {
        disableClose: true,
        panelClass: 'noconnect-modal',
        backdropClass: 'noconnect-backdrop'
      });
    }
  }
}
